/**
 * Twitter OAuth 2.0 PKCE Service
 * Handles client-side OAuth flow for Twitter integration
 */

// Generate random string for PKCE
function generateRandomString(length: number): string {
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let text = '';
  for (let i = 0; i < length; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }
  return text;
}

// Generate SHA256 hash
async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return window.crypto.subtle.digest('SHA-256', data);
}

// Base64 URL encode
function base64urlencode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    str += String.fromCharCode(bytes[i]);
  }
  return btoa(str)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

// Generate PKCE code challenge
async function generateCodeChallenge(verifier: string): Promise<string> {
  const hashed = await sha256(verifier);
  return base64urlencode(hashed);
}

interface TwitterOAuthConfig {
  clientId: string;
  redirectUri: string;
  scopes: string[];
}

class TwitterOAuthService {
  private config: TwitterOAuthConfig | null = null;

  /**
   * Initialize the OAuth service with configuration
   */
  initialize(clientId: string) {
    this.config = {
      clientId,
      redirectUri: window.location.origin + '/',
      scopes: ['tweet.read', 'users.read', 'offline.access']
    };
  }

  /**
   * Start OAuth flow - generates PKCE params and redirects to Twitter
   */
  async startOAuthFlow(walletAddress: string): Promise<void> {
    if (!this.config) {
      throw new Error('Twitter OAuth not initialized. Call initialize() first.');
    }

    // Generate PKCE parameters
    const codeVerifier = generateRandomString(128);
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = generateRandomString(32);

    // Store PKCE params and wallet in sessionStorage for callback
    sessionStorage.setItem('twitter_code_verifier', codeVerifier);
    sessionStorage.setItem('twitter_state', state);
    sessionStorage.setItem('twitter_wallet', walletAddress);

    // Build Twitter OAuth URL
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      scope: this.config.scopes.join(' '),
      state: state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256'
    });

    const authUrl = `https://twitter.com/i/oauth2/authorize?${params.toString()}`;
    
    // Debug logging
    console.log('🐦 Twitter OAuth Config:', {
      clientId: this.config.clientId,
      redirectUri: this.config.redirectUri,
      scopes: this.config.scopes,
      authUrl
    });
    
    // Redirect to Twitter
    window.location.href = authUrl;
  }

  /**
   * Handle OAuth callback after Twitter redirects back
   * Returns { code, verifier, state } if successful
   */
  handleCallback(): { code: string; verifier: string; walletAddress: string } | null {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    // Check for errors
    if (error) {
      console.error('Twitter OAuth error:', error);
      this.cleanup();
      return null;
    }

    // No code means not a callback
    if (!code || !state) {
      return null;
    }

    // Verify state matches
    const storedState = sessionStorage.getItem('twitter_state');
    
    if (state !== storedState) {
      console.error('Twitter OAuth state mismatch');
      this.cleanup();
      return null;
    }

    // Get stored params
    const verifier = sessionStorage.getItem('twitter_code_verifier');
    const walletAddress = sessionStorage.getItem('twitter_wallet');

    if (!verifier || !walletAddress) {
      console.error('Twitter OAuth missing stored params');
      this.cleanup();
      return null;
    }

    // Clean up URL (remove query params)
    window.history.replaceState({}, document.title, window.location.pathname);

    return { code, verifier, walletAddress };
  }

  /**
   * Clean up stored OAuth params
   */
  cleanup(): void {
    sessionStorage.removeItem('twitter_code_verifier');
    sessionStorage.removeItem('twitter_state');
    sessionStorage.removeItem('twitter_wallet');
  }
}

// Export singleton instance
const twitterOAuthService = new TwitterOAuthService();
export default twitterOAuthService;

